import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    CheckCircle2,
    MapPin,
    Sparkles,
    Globe2,
    Plane,
    Building2,
    ListChecks,
    Truck,
    Boxes,
    KeyRound,
    Map,
    PhoneCall,
    Calendar,
    PlayCircle,
} from "lucide-react";
import FloatingScene from "@/components/site/FloatingScene";
import Marquee from "@/components/site/Marquee";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { IconSticker } from "@/components/site/Sticker";
import CartoonSticker from "@/components/site/CartoonStickers";

const trustPoints = [
    { icon: Sparkles, label: "Clear pricing" },
    { icon: ListChecks, label: "Practical coordination" },
    { icon: MapPin, label: "Local Toronto / GTA support" },
];

const audiences = [
    {
        icon: Plane,
        eyebrow: "International",
        title: "Moves to Toronto from abroad",
        body: "Local Toronto-side support before you arrive — coordination, viewings, vendors, and arrival planning.",
        tone: "orange",
    },
    {
        icon: Globe2,
        eyebrow: "Cross-Canada",
        title: "Relocating from another Canadian city",
        body: "Help with local coordination, planning, viewings, and the practical groundwork for a smooth landing.",
        tone: "navy",
    },
    {
        icon: Building2,
        eyebrow: "Local",
        title: "Moving within Toronto / GTA",
        body: "Move-day coordination, packing and cleaning support, and a trusted vendor roster across the GTA.",
        tone: "cream",
    },
];

const homeServices = [
    { icon: PhoneCall, title: "Free Consultation", body: "A 20-minute call to understand your move and the best next step.", slug: "free-consultation" },
    { icon: ListChecks, title: "Relocation Planning", body: "Structured planning for timeline, options, and support needs.", slug: "paid-consultation" },
    { icon: Map, title: "Rental Shortlisting", body: "We narrow down options based on your location, budget, and timing.", slug: "shortlisting" },
    { icon: KeyRound, title: "Viewing on Behalf", body: "We attend local viewings when you cannot, and share practical observations.", slug: "viewing" },
    { icon: Truck, title: "Move-Day Coordination", body: "We coordinate timing, vendors, access, and updates during the agreed block.", slug: "move-day" },
    { icon: Boxes, title: "Packing & Cleaning", body: "We coordinate vendors and helpers to handle packing or cleaning support.", slug: "packing" },
];

const steps = [
    { n: "01", title: "Book a free consultation", body: "Tell us about your move." },
    { n: "02", title: "Choose the right support", body: "Pick the services you need." },
    { n: "03", title: "Confirm scope & pricing", body: "We agree on scope before paid work." },
    { n: "04", title: "We coordinate the tasks", body: "You get clear updates throughout." },
];

const previewPricing = [
    { title: "Free Consultation", price: "Free", note: "Up to 20 min" },
    { title: "Paid Consultation", price: "$75", note: "per hour" },
    { title: "Shortlisting", price: "$150", note: "2 hours" },
    { title: "Viewing on Behalf", price: "$300", note: "4 hours" },
    { title: "Move-Day", price: "From $200", note: "2–10 hour blocks" },
];

export default function Home() {
    return (
        <div data-testid="home-page" className="relative">
            {/* HERO */}
            <section className="relative gradient-warm overflow-hidden">
                {/* decorative floating shapes */}
                <span className="float-y-slow absolute -top-10 left-10 h-24 w-24 rounded-full bg-[#F26B1F]/10 blur-2xl" />
                <span className="float-y absolute top-1/3 right-1/4 h-32 w-32 rounded-full bg-[#0E2A47]/10 blur-3xl" />

                <div className="container-atm relative grid items-center gap-12 py-20 md:grid-cols-12 md:py-28">
                    <div className="md:col-span-6 lg:col-span-7">
                        <Reveal>
                            <span className="eyebrow" data-testid="hero-eyebrow">
                                <span className="h-px w-8 bg-[#F26B1F]" />
                                Toronto · GTA · Worldwide arrivals
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h1
                                data-testid="hero-title"
                                className="mt-6 text-4xl font-semibold leading-[1.02] sm:text-5xl lg:text-6xl xl:text-7xl"
                            >
                                Moving to Toronto or
                                <br className="hidden sm:block" /> within the GTA?
                                <span className="mt-2 block bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                    We help coordinate the move.
                                </span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#475569] md:text-lg">
                                AllTask Moves helps international clients, out-of-town
                                movers, and local GTA residents plan the practical side of
                                moving with clear pricing, structured communication, and
                                trusted local support.
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/book"
                                    className="btn-primary"
                                    data-testid="hero-cta-book"
                                >
                                    Book a Free Consultation <ArrowRight size={18} />
                                </Link>
                                <Link
                                    to="/services"
                                    className="btn-secondary"
                                    data-testid="hero-cta-services"
                                >
                                    View Services
                                </Link>
                            </div>
                        </Reveal>

                        <Stagger className="mt-12 flex flex-wrap gap-4 md:gap-6" delay={0.3}>
                            {trustPoints.map(({ icon: Icon, label }) => (
                                <Item
                                    key={label}
                                    className="inline-flex items-center gap-3 rounded-full border border-[#ECE4D3] bg-white/70 px-4 py-2 text-sm font-semibold text-[#0E2A47] backdrop-blur"
                                >
                                    <span
                                        className="rounded-full bg-[#FDF1E7] p-1.5 text-[#F26B1F]"
                                        data-testid={`trust-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <Icon size={14} strokeWidth={2.2} />
                                    </span>
                                    {label}
                                </Item>
                            ))}
                        </Stagger>
                    </div>

                    <div className="relative md:col-span-6 lg:col-span-5" data-testid="hero-illustration">
                        <FloatingScene />
                    </div>
                </div>
            </section>

            {/* TRAILER VIDEO */}
            <section
                className="section relative overflow-hidden"
                data-testid="home-trailer-section"
            >
                {/* subtle decorative accent — far from copy, desktop only */}
                <CartoonSticker
                    name="boxes"
                    width={84}
                    rotate={-8}
                    className="hidden xl:inline-flex top-12 left-8"
                />
                <CartoonSticker
                    name="keys"
                    width={72}
                    rotate={6}
                    className="hidden xl:inline-flex bottom-16 right-10"
                />

                <div className="container-atm relative">
                    <Reveal>
                        <div className="mx-auto max-w-2xl text-center">
                            <span className="eyebrow inline-flex items-center justify-center gap-1.5">
                                <PlayCircle size={14} strokeWidth={2} /> Watch
                            </span>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl md:text-5xl">
                                See how AllTask Moves helps{" "}
                                <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                    simplify your move.
                                </span>
                            </h2>
                            <p className="mt-5 text-base text-[#475569] md:text-lg">
                                A quick look at how we coordinate the practical side of moving
                                to Toronto or within the GTA.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div
                            className="card-3d mx-auto mt-10 max-w-4xl overflow-hidden bg-[#0E2A47] p-0 ring-1 ring-[#ECE4D3]"
                            data-testid="home-trailer-card"
                        >
                            <div className="relative aspect-video w-full bg-black">
                                <video
                                    className="absolute inset-0 h-full w-full"
                                    src="/assets/media/alltask-moves-trailer.mp4"
                                    poster="/assets/brand/hero-illustration.webp?v=5"
                                    controls
                                    playsInline
                                    preload="metadata"
                                    data-testid="home-trailer-video"
                                >
                                    Sorry, your browser doesn&apos;t support embedded video.
                                </video>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <Link
                                to="/book?service=free_consultation"
                                className="btn-primary inline-flex"
                                data-testid="home-trailer-book-btn"
                            >
                                Book a Free Consultation <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/services"
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E2A47]/80 transition-colors hover:text-[#F26B1F]"
                                data-testid="home-trailer-services-link"
                            >
                                View Services <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MARQUEE */}
            <Marquee />

            {/* PROBLEM */}
            <section className="section relative gradient-cream overflow-hidden">
                {/* one decorative accent only, far from text — desktop only */}
                <CartoonSticker name="boxes" width={110} rotate={-6} className="hidden xl:inline-flex top-10 right-6" />

                <div className="container-atm relative grid items-center gap-12 md:grid-cols-12">
                    <Reveal className="md:col-span-7">
                        <span className="eyebrow">The reality of a move</span>
                        <h2 className="mt-5 text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                            Moving is more than getting from one place to another.
                        </h2>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#475569] md:text-lg">
                            A move can involve timing, vendors, packing, cleaning, building
                            access, viewings, and arrival planning. AllTask Moves helps
                            organize the moving pieces so the process feels clearer and
                            easier to manage.
                        </p>
                    </Reveal>
                    <Stagger className="grid grid-cols-2 gap-4 md:col-span-5">
                        {[
                            { icon: Calendar, label: "Timing & access" },
                            { icon: Truck, label: "Vendors & movers" },
                            { icon: Boxes, label: "Packing & cleaning" },
                            { icon: KeyRound, label: "Move-in handover" },
                        ].map(({ icon: Icon, label }, i) => (
                            <Item
                                key={label}
                                className="card-3d flex items-start gap-3 p-5"
                            >
                                <span className={`rounded-xl p-2 ${i % 2 === 0 ? "bg-[#FDF1E7] text-[#F26B1F]" : "bg-[#0E2A47] text-white"}`}>
                                    <Icon size={20} strokeWidth={1.8} />
                                </span>
                                <p className="mt-1 text-sm font-semibold text-[#0E2A47]">
                                    {label}
                                </p>
                            </Item>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* WHO WE HELP */}
            <section className="section relative">
                {/* corner accent only */}
                <CartoonSticker name="house" width={96} rotate={5} className="hidden xl:inline-flex top-6 left-4" />
                <div className="container-atm relative">
                    <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
                        <Reveal>
                            <span className="eyebrow">Who we help</span>
                            <h2 className="mt-4 max-w-2xl text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                                Three kinds of moves. One coordinated process.
                            </h2>
                        </Reveal>
                    </div>

                    <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
                        {audiences.map((a, i) => {
                            const isOrange = a.tone === "orange";
                            const isNavy = a.tone === "navy";
                            return (
                                <Item key={a.title}>
                                    <motion.div
                                        whileHover={{ y: -8 }}
                                        transition={{ duration: 0.4 }}
                                        className={`relative h-full overflow-hidden rounded-3xl p-8 ${
                                            isNavy
                                                ? "bg-[#0E2A47] text-white"
                                                : isOrange
                                                  ? "bg-gradient-to-br from-[#FDF1E7] to-[#FFFBF5] text-[#0E2A47]"
                                                  : "bg-white text-[#0E2A47]"
                                        }`}
                                        style={{
                                            boxShadow:
                                                "0 28px 50px -25px rgba(14,42,71,0.22), 0 8px 16px -8px rgba(14,42,71,0.08)",
                                        }}
                                        data-testid={`audience-card-${i}`}
                                    >
                                        {/* cartoon sticker accent */}
                                        <CartoonSticker
                                            name={["mover", "house", "boxes"][i]}
                                            width={92}
                                            rotate={[-6, 8, -3][i]}
                                            className="-bottom-4 -right-2 opacity-90"
                                        />
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`inline-flex rounded-2xl p-3 ${
                                                    isNavy
                                                        ? "bg-[#F26B1F] text-white"
                                                        : "bg-[#0E2A47] text-white"
                                                }`}
                                            >
                                                <a.icon size={22} strokeWidth={1.6} />
                                            </div>
                                            <span
                                                className={`eyebrow ${
                                                    isNavy
                                                        ? "!text-[#F26B1F]"
                                                        : ""
                                                }`}
                                            >
                                                {a.eyebrow}
                                            </span>
                                        </div>
                                        <h3 className="mt-5 text-2xl font-semibold leading-tight">
                                            {a.title}
                                        </h3>
                                        <p
                                            className={`mt-3 text-[15px] leading-relaxed ${
                                                isNavy ? "text-white/75" : "text-[#475569]"
                                            }`}
                                        >
                                            {a.body}
                                        </p>
                                    </motion.div>
                                </Item>
                            );
                        })}
                    </Stagger>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="section relative gradient-cream overflow-hidden">
                {/* single corner accent */}
                <CartoonSticker name="lamp" width={80} rotate={-10} className="hidden xl:inline-flex bottom-6 left-4" />
                <div className="container-atm relative">
                    <Reveal className="max-w-3xl">
                        <span className="eyebrow">Practical support</span>
                        <h2 className="mt-4 text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                            For the parts of moving that get stressful.
                        </h2>
                    </Reveal>

                    <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {homeServices.map((s, i) => (
                            <Item key={s.title}>
                                <motion.div
                                    whileHover={{ y: -8 }}
                                    transition={{ duration: 0.35 }}
                                    className="h-full"
                                >
                                    <Link
                                        to={`/services/${s.slug}`}
                                        className="card-3d group relative flex h-full flex-col overflow-hidden p-8"
                                        data-testid={`service-card-${i}`}
                                    >
                                        <span className="absolute inset-x-0 -top-1 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#F26B1F] to-[#FFA66E] transition-transform duration-500 group-hover:scale-x-100" />
                                        <div className="flex items-center justify-between">
                                            <span className="rounded-xl bg-[#FDF1E7] p-3 text-[#F26B1F] transition-colors group-hover:bg-[#F26B1F] group-hover:text-white">
                                                <s.icon size={22} strokeWidth={1.6} />
                                            </span>
                                            <span className="text-xs font-bold uppercase tracking-widest text-[#0E2A47]/30">
                                                0{i + 1}
                                            </span>
                                        </div>
                                        <h3 className="mt-6 text-xl font-semibold">
                                            {s.title}
                                        </h3>
                                        <p className="mt-2 flex-1 text-[15px] leading-relaxed text-[#475569]">
                                            {s.body}
                                        </p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F26B1F] transition-all group-hover:gap-3">
                                            Learn more <ArrowRight size={14} />
                                        </span>
                                    </Link>
                                </motion.div>
                            </Item>
                        ))}
                    </Stagger>

                    <Reveal delay={0.1} className="mt-12">
                        <Link
                            to="/services"
                            className="btn-secondary"
                            data-testid="services-explore-btn"
                        >
                            Explore Services <ArrowRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="section relative">
                <IconSticker icon={Truck} tone="orange" size={48} rotate={-8} className="hidden xl:flex absolute right-6 top-10 truck-roll" />
                <div className="container-atm">
                    <div className="grid items-end gap-10 md:grid-cols-12">
                        <Reveal className="md:col-span-5">
                            <span className="eyebrow">How it works</span>
                            <h2 className="mt-4 text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                                Four steps. No surprises.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1} className="md:col-span-7">
                            <p className="text-base text-[#475569] md:text-lg">
                                We confirm scope and pricing before paid work begins. You stay
                                in control of every decision throughout your move.
                            </p>
                        </Reveal>
                    </div>

                    <Stagger className="mt-14 grid gap-5 md:grid-cols-4">
                        {steps.map((s, i) => (
                            <Item key={s.n}>
                                <motion.div
                                    whileHover={{ y: -8, rotate: -1 }}
                                    transition={{ duration: 0.35 }}
                                    className="card-3d relative h-full p-7"
                                    data-testid={`step-${i}`}
                                >
                                    <span className="absolute -top-3 -right-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F26B1F] font-display text-base font-bold text-white shadow-lg">
                                        {i + 1}
                                    </span>
                                    <p className="font-display text-3xl font-semibold text-[#0E2A47]">
                                        {s.n}
                                    </p>
                                    <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#475569]">
                                        {s.body}
                                    </p>
                                </motion.div>
                            </Item>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* PRICING PREVIEW */}
            <section className="section relative gradient-cream overflow-hidden">
                <div className="container-atm relative">
                    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                        <Reveal>
                            <span className="eyebrow">Clear pricing</span>
                            <h2 className="mt-4 text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                                Simple time-blocks. Confirmed scope.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link
                                to="/pricing"
                                className="btn-secondary self-start md:self-end"
                                data-testid="pricing-view-btn"
                            >
                                View Pricing <ArrowRight size={18} />
                            </Link>
                        </Reveal>
                    </div>

                    <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
                        {previewPricing.map((p, i) => (
                            <Item key={p.title}>
                                <motion.div
                                    whileHover={{ y: -8 }}
                                    transition={{ duration: 0.35 }}
                                    className="card-3d h-full p-6"
                                    data-testid={`pricing-preview-${i}`}
                                >
                                    <p className="text-xs font-bold uppercase tracking-widest text-[#F26B1F]">
                                        {p.title}
                                    </p>
                                    <p className="mt-3 font-display text-3xl font-semibold text-[#0E2A47]">
                                        {p.price}
                                    </p>
                                    <p className="mt-1 text-sm text-[#475569]">{p.note}</p>
                                </motion.div>
                            </Item>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="section relative">
                <div className="container-atm">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[40px] gradient-navy px-8 py-16 text-white md:px-16 md:py-20">
                            {/* single decorative accent — far corner */}
                            <IconSticker icon={Truck} tone="orange" size={56} rotate={-8} className="absolute right-10 top-10 truck-roll hidden lg:flex" />

                            <div className="relative grid items-center gap-10 md:grid-cols-12">
                                <div className="md:col-span-7">
                                    <span className="eyebrow !text-[#F26B1F]">
                                        Get started
                                    </span>
                                    <h2 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
                                        Planning a move? Start with a free consultation.
                                    </h2>
                                    <p className="mt-5 max-w-xl text-base text-white/75 md:text-lg">
                                        Tell us where you're moving from, where you're moving
                                        to, and what kind of support you need. We'll help
                                        you understand the right next step.
                                    </p>
                                </div>
                                <div className="md:col-span-5 md:text-right">
                                    <Link
                                        to="/book"
                                        className="btn-primary !shadow-none"
                                        data-testid="final-cta-book"
                                    >
                                        Book Free Consultation <ArrowRight size={18} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
